import { Fragment, FunctionComponent, Suspense, lazy, useRef, useState } from 'react';
import { useMatch, useNavigate } from 'react-router-dom';
import { t } from '@/utils/i18n';
import { getParams, isCYOMode } from '@/utils/params';
import { LoadingOverlay } from '@/components/common/LoadingOverlay';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalTitle,
  ModalTypes,
} from '@/components/common/modal/modal';

export const SORT_TYPE_DEFAULT = 0;
export const SORT_TYPE_PRICE = 1;
export const SORT_TYPE_PRICE_REVERSE = 2;
export const SORT_TYPE_NEW_PRODUCTS = 3;
export type SortType =
  | typeof SORT_TYPE_DEFAULT
  | typeof SORT_TYPE_PRICE
  | typeof SORT_TYPE_PRICE_REVERSE
  | typeof SORT_TYPE_NEW_PRODUCTS;

function getInitialDiscountQuantity() {
  if (isCYOMode()) {
    const discountQuantityParam = Number.parseInt(getParams().discountQuantity || '');
    return discountQuantityParam > 0 ? discountQuantityParam : 1;
  }

  return -1;
}

const ProductTypeSelector = lazy(
  () =>
    import(
      /* webpackChunkName: 'product-type-selector', webpackPrefetch: true */ './ProductTypeSelector'
    )
);

export const LIST_ROUTE = '/productTypes';
export const DISCOUNT_QUANTITY_ROUTE = `${LIST_ROUTE}/quantity`;
export const FILTER_ROUTE = `${LIST_ROUTE}/filter`;
export const BRANDS_ROUTE = `${FILTER_ROUTE}/brands`;

export const AsyncProductTypeSelector: FunctionComponent = () => {
  const navigate = useNavigate();
  const isActive = Boolean(useMatch({ path: LIST_ROUTE, end: false }));

  const scrollStartIndex = useRef(0);
  // quantity that is used for API calls
  const [discountQuantity, setDiscountQuantity] = useState(getInitialDiscountQuantity);
  const [sortType, setSortType] = useState<SortType>(SORT_TYPE_DEFAULT);

  const handleClose = () => navigate('/');

  const title = t('productTypePanel.title');
  return (
    <Modal
      type={ModalTypes.panel}
      open={isActive}
      title={title}
      className="pt-selector"
      onClose={handleClose}
    >
      <Suspense
        fallback={
          <Fragment>
            <ModalHeader>
              <ModalTitle>{title}</ModalTitle>
            </ModalHeader>
            <ModalContent>
              <LoadingOverlay />
            </ModalContent>
          </Fragment>
        }
      >
        <ProductTypeSelector
          scrollStartIndex={scrollStartIndex}
          discountQuantity={discountQuantity}
          setDiscountQuantity={setDiscountQuantity}
          sortType={sortType}
          updateSort={setSortType}
          onClose={handleClose}
        />
      </Suspense>
    </Modal>
  );
};
